import {
  Avatar,
  Badge,
  Box,
  BoxProps,
  Button,
  CloseButton,
  Divider,
  Drawer,
  DrawerContent,
  Flex,
  FlexProps,
  HStack,
  Icon,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure
} from '@chakra-ui/react';
import { BiRegistered, BiStore, BiSupport } from 'react-icons/bi';
import { Link as DefaultLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiBell,
  FiBriefcase,
  FiChevronDown,
  FiMenu,
  FiSettings,
  FiTruck,
  FiUsers
} from 'react-icons/fi';
import React, { ReactNode, useEffect } from 'react';
import { RiMapPinUserLine, RiMenuFoldLine, RiMenuUnfoldLine } from 'react-icons/ri';
import { TbCode, TbTrolley, TbTruckDelivery } from 'react-icons/tb';
import { logoutSuccess, toggleSideMenu } from '../store/reducers';
import { useAppDispatch, useAppSelector } from '../hooks';

import { AiOutlineDashboard } from 'react-icons/ai';
import { AppDispatch } from '../store';
import CompanyPlaceholder from '../theme/assets/company-placeholder.png';
import { DonateButton } from '../components';
import { GrMapLocation } from 'react-icons/gr';
import { IconType } from 'react-icons';
import { ReactText } from 'react';
import { checkIfKeyExist } from '../utils';
import { getUser } from '../store/actions';

interface LinkItemProps {
  name: string | any;
  route?: string;
  icon: IconType;
  is_beta?: boolean;
}
const LinkItems: Array<LinkItemProps> = [
  { name: 'Dashboard', route: '/', is_beta: false, icon: AiOutlineDashboard },
  { name: 'Deliveries', route: '/deliveries', is_beta: false, icon: TbTruckDelivery },
  { name: 'Parcels Deliveries', route: '/parcels', is_beta: false, icon: TbTrolley },
  { name: 'Customers', route: '/customers', is_beta: false, icon: RiMapPinUserLine },
  { name: 'Services', route: '/services', is_beta: false, icon: FiBriefcase },
  { name: 'Users', route: '/users', is_beta: false, icon: FiUsers },
  { name: 'Vehicles', route: '/vehicles', is_beta: false, icon: FiTruck },
  { name: 'Pickup Points', route: '/pickup-points', is_beta: false, icon: GrMapLocation },
  { name: 'Settings', route: '/settings', is_beta: false, icon: FiSettings },
];

export default function AppContainer({
  children,
}: {
  children: ReactNode;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { isAuthenticated } = useAppSelector(state => state.authReducer);
  const { is_drawer_open } = useAppSelector(state => state.globalReducer);

  const navigate = useNavigate();

  const location = useLocation();

  const validate = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/forgot-password' || location.pathname === '/reset-password';

  useEffect(() => {
    if (!isAuthenticated) return navigate('/login', { replace: true });
  }, [isAuthenticated]);

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.100', 'gray.900')}>
      {isAuthenticated &&
        <SidebarContent
          onClose={() => onClose}
          display={{ base: 'none', md: 'block' }}
        />}
      {isAuthenticated &&
        <Drawer
          autoFocus={false}
          isOpen={isOpen}
          placement="left"
          onClose={onClose}
          returnFocusOnClose={false}
          onOverlayClick={onClose}
          size="full">
          <DrawerContent>
            <SidebarContent onClose={onClose} />
          </DrawerContent>
        </Drawer>}
      {/* mobilenav */}
      {isAuthenticated && <MobileNav onOpen={onOpen} onClose={onClose} />}
      <Box ml={{ base: 0, md: isAuthenticated && (is_drawer_open ? 60 : 20) }} p={isAuthenticated ? '4' : '0'} pt={validate ? 0 : 24}>
        {children}
      </Box>
    </Box>
  );
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
}

const SidebarContent = ({ onClose, ...rest }: SidebarProps) => {

  const { company } = useAppSelector(state => state.companyReducer);
  const { user } = useAppSelector(state => state.userReducer);
  const { is_drawer_open } = useAppSelector(state => state.globalReducer);

  return (
    <Box
      transition="0.1s ease"
      bg={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: is_drawer_open ? 60 : 20 }}
      pos="fixed"
      h="full"
      style={{
        overflow: 'auto'
      }}
      {...rest}>
      <Flex h="20" alignItems="center" mx="6" justifyContent="space-between">
        <Stack alignItems={'center'}>
          <HStack>
            <Image
              boxSize='40px'
              alt={'logo'}
              src={CompanyPlaceholder}
            />
            {is_drawer_open && <Text textTransform={'capitalize'} fontSize={'md'} fontWeight={'semibold'} noOfLines={1} title={company?.name}>{company?.name}</Text>}
          </HStack>
        </Stack>
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      <Divider mb={2} />
      {
        user?.company_id ?
          <>
            {
              LinkItems.filter(item => company?.is_beta_tester == true && item?.is_beta == true ? item : item?.is_beta == false).map((link) => (
                <NavItem key={link.name} icon={link.icon} to={link.route} onClose={onClose} is_beta_tester={link?.is_beta}>
                  {link.name}
                </NavItem>
              ))
            }
            < Divider />
            <Stack bg={'#f5f5f5'} m={1} borderRadius={4}>
              {/* <NavItem to={'/company-register'}>
                Register company?
              </NavItem> */}
              <NavItem icon={BiSupport} to={'/help-support'} onClose={onClose}>
                Help & Support
              </NavItem>
            </Stack>
            <Divider />
            <NavItem icon={TbCode} to={'/developer'} onClose={onClose}>
              Developer Hub
            </NavItem>
          </>
          :
          <NavItem icon={BiRegistered} to={'/company/add'} onClose={onClose}>
            Business details
          </NavItem>
      }
    </Box>
  );
};

interface NavItemProps extends FlexProps {
  icon?: IconType;
  children: ReactText;
  to?: string | any;
  is_beta_tester?: boolean;
  onClose: () => void;
}
const NavItem = ({ icon, children, to, onClose, is_beta_tester, ...rest }: NavItemProps) => {
  const is_active: boolean = window.location.pathname === to;
  const { is_drawer_open } = useAppSelector(state => state.globalReducer);
  return (
    <Link href="#" style={{ textDecoration: 'none' }} _focus={{ boxShadow: 'none' }} as={DefaultLink} to={to} onClick={onClose}>
      <Tooltip isDisabled={is_drawer_open} label={children} placement='right' hasArrow>
        <Flex
          align="center"
          px="4"
          py='3'
          mx="4"
          borderRadius="lg"
          role="group"
          cursor="pointer"
          _hover={{
            bg: '#fff8e1',
            color: '#212121',
          }}
          bgColor={is_active ? 'yellow.400' : 'inherit'}
          mb={1}
          {...rest}>
          {icon ? (
            <Icon
              mr="4"
              fontSize={is_drawer_open ? 18 : 20}
              _groupHover={{
                color: '#212121',
              }}
              as={icon}
            />
          ) : <></>}
          {is_drawer_open && children}
          {is_drawer_open && is_beta_tester && <Badge ml={1} colorScheme={'green'}>beta</Badge>}
        </Flex>
      </Tooltip>
    </Link>
  );
};

interface MobileProps extends FlexProps {
  onOpen: () => void;
  onClose: () => void;
}
//eslint-disable-next-line
const MobileNav = ({ onOpen, onClose, ...rest }: MobileProps) => {
  const { user } = useAppSelector(state => state.userReducer);
  const { isAuthenticated } = useAppSelector(state => state.authReducer);
  // const { is_company } = useAppSelector(state => state.companyReducer);
  const { is_drawer_open } = useAppSelector(state => state.globalReducer);
  const dispatch: AppDispatch = useAppDispatch();
  let count = 0;

  if (!user?.company_id) count = 1;

  useEffect(() => {
    if (checkIfKeyExist(user, 'user_id') === false && isAuthenticated) dispatch(getUser());
    // dispatch(companyExistance());
  }, []);

  return (
    <Flex
      ml={{ base: 0, md: is_drawer_open ? 60 : 20 }}
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={useColorModeValue('white', 'gray.900')}
      borderBottomWidth="1px"
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      justifyContent={{ base: 'space-between', md: 'flex-end' }}
      position={'fixed'}
      style={{
        width: '-webkit-fill-available',
        minWidth: '-moz-available'
      }}
      zIndex={4}
      {...rest}>
      <IconButton
        display={{ base: 'flex', md: 'none' }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
      />

      {/* <Text
        display={{ base: 'flex', md: 'none' }}
        fontSize="2xl"
        fontFamily="monospace"
        fontWeight="bold">
        Logo
      </Text> */}
      <HStack spacing={{ base: '0', md: '6' }}>
        <IconButton
          display={{ base: 'none', md: 'flex' }}
          position={'absolute'}
          left={0}
          ml={4}
          aria-label='Search database'
          icon={is_drawer_open ? <RiMenuFoldLine /> : <RiMenuUnfoldLine />}
          onClick={() => dispatch(toggleSideMenu(!is_drawer_open))}
        />
        <Flex alignItems={'center'}>
          <DonateButton />
        </Flex>
        <Flex alignItems={'center'}>
          <Button
            leftIcon={<BiStore size={22} />}
            colorScheme='yellow'
            variant='solid'
            as={DefaultLink}
            to='/store'
          >
            Market Place
          </Button>
          <Menu>
            <MenuButton
              py={2}
              transition="all 0.3s"
              _focus={{ boxShadow: 'none' }}>
              <HStack>
                <IconButton
                  size="lg"
                  variant="ghost"
                  aria-label="open menu"
                  icon={<FiBell />}
                />
                <Badge style={{ marginInlineStart: -10 }} colorScheme={count ? 'red' : 'green'}>{count}</Badge>
              </HStack>
            </MenuButton>
            <MenuList
              bg={useColorModeValue('white', 'gray.900')}
              borderColor={useColorModeValue('gray.200', 'gray.700')}>
              {!user?.company_id && <MenuItem><Link style={{ textDecoration: 'none' }} as={DefaultLink} to='/company/add'>Add your business details</Link></MenuItem>}
              {count === 0 && <Text ml={3}>No notifications</Text>}
            </MenuList>
          </Menu>
        </Flex>
        <Flex alignItems={'center'}>
          <Menu>
            <MenuButton
              py={2}
              transition="all 0.3s"
              _focus={{ boxShadow: 'none' }}>
              <HStack>
                <VStack
                  display={{ base: 'none', md: 'flex' }}
                  alignItems="flex-start"
                  spacing="1px"
                  ml="2">
                  <Text fontSize="md" textTransform='capitalize'>{user?.first_name || 'Name'} {user?.last_name || 'Surname'}</Text>
                  {/* <Text fontSize="xs" color="gray.600" textTransform='capitalize'>
                    {user?.role || 'Role'}
                  </Text> */}
                </VStack>
                <Avatar
                  size={'sm'}
                  name={`${user?.first_name} ${user?.last_name}`}
                  src={user?.img_url}
                />
                <Box display={{ base: 'none', md: 'flex' }}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList
              bg={useColorModeValue('white', 'gray.900')}
              borderColor={useColorModeValue('gray.200', 'gray.700')}>
              <MenuItem>Profile</MenuItem>
              <MenuItem>Settings</MenuItem>
              <MenuItem>Billing</MenuItem>
              <MenuDivider />
              <MenuItem
                onClick={() => {
                  localStorage.clear();
                  dispatch(logoutSuccess());
                }
                }
              >Sign out</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  );
};